import { IonContent, IonPage } from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { Btn, FloatingBalls, Icon } from '../components/chancey';
import { safeAreaOffset, safeAreaPaddingX, withSafeArea } from '../components/layout/safeArea';
import { useUserPrefs } from '../hooks/useUserPrefs';
import {
  init as pushInit,
  isPushConfigured,
  requestPermission as requestPushPermission,
} from '../services/pushService';
import { track } from '../services/analyticsService';
import { NOTIFICATION_PREF_ROWS, type NotificationPrefKey } from '../notifications/catalog';
import { useChanceyTheme } from '../theme/ThemeContext';

/** Progress labels: Welcome → How it works → Reminders. */
const STEP_LABELS: string[] = ['Welcome', 'How it works', 'Reminders'];

const STEP_WELCOME = 0;
const STEP_HOW = 1;
/** Notification soft-ask step. */
const STEP_REMINDERS = 2;

const OnboardingPage: React.FC = () => {
  const { c, typ } = useChanceyTheme();
  const [step, setStep] = useState(0);

  const wrap = (content: React.ReactNode) => (
    <div className="relative flex h-full flex-col" style={{ background: c.bg }}>
      <div
        className="flex items-center gap-2.5 pb-2"
        style={{ ...safeAreaPaddingX(20), paddingTop: safeAreaOffset('top', 12, 58) }}
      >
        {step > 0 && (
          <button
            type="button"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full shadow-sm"
            style={{ background: c.surface, color: c.ink, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
            onClick={() => setStep(step - 1)}
            aria-label="Back"
          >
            {Icon.back(18, c.ink)}
          </button>
        )}
        <div className="flex flex-1 gap-1">
          {STEP_LABELS.map((_, i) => (
            <div
              key={i}
              className="h-1 flex-1 rounded-sm transition-colors"
              style={{ background: i <= step ? c.ink : c.surfaceAlt }}
            />
          ))}
        </div>
        <div
          className="min-w-8 text-right font-mono text-[11px]"
          style={{ color: c.inkMuted, fontFamily: typ.mono }}
        >
          {step + 1}/{STEP_LABELS.length}
        </div>
      </div>
      {content}
      <div
        className="pointer-events-none absolute bottom-0 left-0 right-0 z-[60] flex h-[34px] items-end justify-center pb-2"
        style={{
          height: withSafeArea('bottom', 34),
          paddingBottom: `max(${withSafeArea('bottom')}, 8px)`,
        }}
      >
        <div className="h-[5px] w-[139px] rounded-full bg-black/25" />
      </div>
    </div>
  );

  if (step === STEP_WELCOME) {
    return (
      <IonPage>
        <IonContent fullscreen scrollY={false} style={{ '--background': c.bg }}>
          {wrap(
            <div
              className="flex flex-1 flex-col overflow-hidden px-6 pb-9 pt-2.5"
              style={{ paddingBottom: withSafeArea('bottom', 36) }}
            >
              <div className="relative flex min-h-[260px] flex-1 items-center justify-center">
                <FloatingBalls c={c} typ={typ} size="lg" centerLabel="7" />
              </div>
              <div>
                <div
                  className="mb-2 text-xs font-semibold uppercase tracking-[0.14em]"
                  style={{ color: c.inkMuted, fontFamily: typ.body }}
                >
                  Welcome to Chancey
                </div>
                <div
                  className="text-[40px] leading-[1.05]"
                  style={{
                    fontFamily: typ.display,
                    fontWeight: typ.displayWeight,
                    letterSpacing: typ.displayLetter,
                    color: c.ink,
                  }}
                >
                  Your pocket-sized lottery{' '}
                  <em style={{ fontStyle: 'italic', color: c.accent }}>sidekick</em>.
                </div>
                <p
                  className="mt-3.5 text-[15px] leading-relaxed"
                  style={{ color: c.inkSoft, fontFamily: typ.body }}
                >
                  Scan your tickets, follow each drawing, and see which numbers show up more often.
                </p>
                <Btn
                  c={c}
                  typ={typ}
                  variant="primary"
                  style={{ width: '100%', marginTop: 24, padding: '16px' }}
                  onClick={() => setStep(STEP_HOW)}
                  icon={Icon.arrow(18, c.bg)}
                >
                  Let&apos;s go
                </Btn>
              </div>
            </div>
          )}
        </IonContent>
      </IonPage>
    );
  }

  if (step === STEP_HOW) {
    return (
      <HowItWorksStep
        c={c}
        typ={typ}
        wrap={wrap}
        onContinue={() => setStep(STEP_REMINDERS)}
        ctaLabel="Continue"
      />
    );
  }

  if (step === STEP_REMINDERS) {
    return <ReminderSoftAskStep wrap={wrap} />;
  }

  return null;
};

/**
 * Slide between Welcome and sign-in. Teaches the three things a new
 * user needs to know before committing to sign-in: that Chancey reads
 * physical tickets, that the Rules tab is a stats playground (with a
 * truthful "generation coming soon" pill — SMART_PICK is mock today), and
 * that we send draw reminders.
 */
const HowItWorksStep: React.FC<{
  c: ReturnType<typeof useChanceyTheme>['c'];
  typ: ReturnType<typeof useChanceyTheme>['typ'];
  wrap: (content: React.ReactNode) => React.ReactElement;
  onContinue: () => void;
  ctaLabel?: string;
}> = ({ c, typ, wrap, onContinue, ctaLabel = 'Continue' }) => {
  const tiles: Array<{
    icon: React.ReactNode;
    iconBg: string;
    iconFg: string;
    label: string;
    desc: string;
    pill?: string;
    delay: string;
  }> = [
    {
      icon: Icon.cam(18, c.bg),
      iconBg: c.ink,
      iconFg: c.bg,
      label: 'Scan your tickets',
      desc: 'Take a photo of your ticket. We read the numbers and tell you if you won.',
      delay: '0s',
    },
    {
      icon: Icon.wand(18, c.accentInk),
      iconBg: c.accent,
      iconFg: c.accentInk,
      label: 'Picks from stats',
      desc: "Add simple rules (for example, hot numbers). Use them to plan your next ticket.",
      pill: 'Auto-picks coming soon',
      delay: '0.2s',
    },
    {
      icon: Icon.bell(18, '#fff'),
      iconBg: c.lucky,
      iconFg: '#fff',
      label: 'Never miss a draw',
      desc: 'Get a heads-up before each drawing. Skip for now if you want—you can turn reminders on later in Settings.',
      delay: '0.4s',
    },
  ];

  return (
    <IonPage>
      <IonContent fullscreen style={{ '--background': c.bg }}>
        {wrap(
          <div
            className="flex flex-1 flex-col px-6 pb-9 pt-5"
            style={{ paddingBottom: withSafeArea('bottom', 36) }}
          >
            <style>{`
              @keyframes chancey-tile-bob {
                from { transform: translateY(-2px); }
                to { transform: translateY(2px); }
              }
            `}</style>
            <div className="mb-1 flex items-center gap-2">
              {Icon.sparkle(18, c.accent)}
              <div
                className="text-xs font-bold uppercase tracking-[0.12em]"
                style={{ color: c.accent, fontFamily: typ.body }}
              >
                How it works
              </div>
            </div>
            <div
              className="text-[30px] leading-tight"
              style={{
                fontFamily: typ.display,
                fontWeight: typ.displayWeight,
                letterSpacing: typ.displayLetter,
                color: c.ink,
              }}
            >
              Three ways Chancey helps
            </div>
            <p
              className="mt-2.5 text-sm leading-relaxed"
              style={{ color: c.inkSoft, fontFamily: typ.body }}
            >
              A quick tour of what to try first.
            </p>

            <div
              className="mt-5 flex flex-col gap-2.5"
              role="group"
              aria-label="What Chancey can do for you"
            >
              {tiles.map((t) => (
                <div
                  key={t.label}
                  className="flex items-center gap-3 rounded-[16px] border p-3.5"
                  style={{ background: c.surface, borderColor: c.line }}
                >
                  <div
                    aria-hidden="true"
                    className="chancey-decor-motion flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                    style={{
                      background: t.iconBg,
                      color: t.iconFg,
                      animation: `chancey-tile-bob 2.8s ease-in-out ${t.delay} infinite alternate`,
                    }}
                  >
                    {t.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <div
                        className="text-[15px] font-semibold"
                        style={{ color: c.ink, fontFamily: typ.body }}
                      >
                        {t.label}
                      </div>
                      {t.pill && (
                        <span
                          className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em]"
                          style={{
                            background: c.accent2,
                            color: c.ink,
                            fontFamily: typ.body,
                          }}
                        >
                          {t.pill}
                        </span>
                      )}
                    </div>
                    <div
                      className="mt-0.5 text-[13px] leading-snug"
                      style={{ color: c.inkSoft, fontFamily: typ.body }}
                    >
                      {t.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex-1" />
            <Btn
              c={c}
              typ={typ}
              variant="primary"
              style={{ width: '100%', padding: '16px', marginTop: 16 }}
              onClick={onContinue}
              icon={Icon.arrow(18, c.bg)}
            >
              {ctaLabel}
            </Btn>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

/**
 * Final onboarding slide. Asks the user to enable draw-night reminders
 * (Powerball Mon/Wed/Sat, Mega Millions Tue/Fri) BEFORE sign-in, so the
 * native permission prompt lives at the moment the user's intent is
 * clearest. Retention research (and App Review guidance) both say: never
 * prompt on cold launch.
 *
 * Because sign-in hasn't happened yet, we can't POST to /v1/me/push/prefs
 * here — there's no JWT. Instead we persist the user's opt-in locally via
 * `useUserPrefs`. The Gate effect in App.tsx picks this up after the user
 * signs in and syncs it to OneSignal via pushLogin + a follow-up prefs
 * call (see App.tsx for the sync logic).
 */
const ReminderSoftAskStep: React.FC<{ wrap: (content: React.ReactNode) => React.ReactElement }> = ({ wrap }) => {
  const { c, typ, completeOnboarding, replayMode } = useChanceyTheme();
  const { prefs, setPartial } = useUserPrefs();
  const [busy, setBusy] = useState(false);
  const pushConfigured = isPushConfigured();

  // Pre-load the OneSignal SDK the moment this slide mounts. Without this
  // the first click on "Turn on notifications" runs: dynamic import +
  // SDK CDN fetch + init handshake + Notifications.requestPermission()
  // within a single user-activation window — Chrome and Safari will drop
  // the native permission prompt if the chain takes too long. Priming the
  // SDK in advance keeps the click → prompt path a single microtask.
  useEffect(() => {
    if (!pushConfigured) return;
    void pushInit();
  }, [pushConfigured]);

  // The default NotifPrefs (useUserPrefs.DEFAULT_PREFS) already has draws+jackpot+weekly on.
  // We keep the same shape here so an opt-in writes the full pref set at
  // once — the server-side sync after sign-in doesn't have to guess.
  const toggleState = prefs.notif;

  const Row: React.FC<{ label: string; onKey: NotificationPrefKey; desc: string }> = ({ label, onKey, desc }) => {
    const on = toggleState[onKey];
    return (
      <button
        type="button"
        onClick={() => {
          setPartial({ notif: { [onKey]: !on } });
          track('notification_pref_change', { source: onKey, outcome: !on ? 'on' : 'off' });
        }}
        className="flex w-full items-center justify-between rounded-[16px] border p-4 text-left"
        style={{
          background: on ? c.surface : c.surfaceAlt,
          borderColor: on ? c.accent : c.line,
          color: c.ink,
        }}
      >
        <div>
          <div className="text-[15px] font-semibold" style={{ fontFamily: typ.body }}>
            {label}
          </div>
          <div className="mt-0.5 text-[12px]" style={{ color: c.inkSoft, fontFamily: typ.body }}>
            {desc}
          </div>
        </div>
        <div
          className="relative h-[26px] w-11 shrink-0 rounded-[13px] transition-colors"
          style={{ background: on ? c.accent : c.line }}
          aria-hidden
        >
          <span
            className="absolute top-0.5 h-[22px] w-[22px] rounded-full bg-white shadow transition-[left]"
            style={{ left: on ? 20 : 2 }}
          />
        </div>
      </button>
    );
  };

  const onTurnOn = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Fire the OS permission prompt. Users who deny here end onboarding
      // with no tags set — the SettingsPage "Enable in system settings"
      // link is the recovery path. We don't error on denial because the
      // user's already downstream of the CTA.
      if (pushConfigured) {
        await requestPushPermission();
      }
    } catch {
      /* ignore: permission API shouldn't throw, but don't block onboarding */
    } finally {
      track('onboarding_complete', { source: 'notifications', outcome: 'enabled' });
      completeOnboarding();
    }
  };

  const onSkip = () => {
    if (replayMode) {
      track('onboarding_complete', { source: 'settings_replay', outcome: 'done' });
      completeOnboarding();
      return;
    }
    // "Maybe later" = turn OFF all notification prefs so we're not holding
    // onto defaults the user never agreed to. They can still opt in from
    // Settings, which will prompt for permission then.
    setPartial({ notif: { draws: false, jackpot: false, results: false, weekly: false } });
    track('onboarding_complete', { source: 'notifications', outcome: 'skipped' });
    completeOnboarding();
  };

  return (
    <IonPage>
      <IonContent fullscreen style={{ '--background': c.bg }}>
        {wrap(
          <div
            className="flex flex-1 flex-col px-6 pb-9 pt-5"
            style={{ paddingBottom: withSafeArea('bottom', 36) }}
          >
            <div className="mb-1 flex items-center gap-2">
              {Icon.sparkle(18, c.accent)}
              <div
                className="text-xs font-bold uppercase tracking-[0.12em]"
                style={{ color: c.accent, fontFamily: typ.body }}
              >
                Stay in the loop
              </div>
            </div>
            <div
              className="text-[30px] leading-tight"
              style={{
                fontFamily: typ.display,
                fontWeight: typ.displayWeight,
                letterSpacing: typ.displayLetter,
                color: c.ink,
              }}
            >
              Want draw-night reminders?
            </div>
            <p
              className="mt-2.5 text-sm leading-relaxed"
              style={{ color: c.inkSoft, fontFamily: typ.body }}
            >
              A nudge a few hours before each Powerball and Mega Millions draw. You can
              tweak these anytime in Settings.
            </p>
            {!pushConfigured && (
              <p
                className="mt-2 text-[12px] leading-relaxed"
                style={{ color: c.inkMuted, fontFamily: typ.body }}
              >
                We&apos;ll save your choices now. You can enable delivery from Settings when
                notifications are available.
              </p>
            )}

            <div className="mt-6 flex flex-col gap-2.5">
              {NOTIFICATION_PREF_ROWS.map((row) => (
                <Row
                  key={row.key}
                  label={row.label}
                  onKey={row.key}
                  desc={row.onboardingDescription}
                />
              ))}
            </div>

            <div className="flex-1" />
            <Btn
              c={c}
              typ={typ}
              variant="lucky"
              style={{ width: '100%', padding: '16px', marginTop: 16 }}
              onClick={() => void onTurnOn()}
              disabled={busy}
              icon={Icon.sparkle(18, '#fff')}
            >
              {busy
                ? 'Enabling…'
                : pushConfigured
                  ? 'Turn on notifications'
                  : 'Save reminder choices'}
            </Btn>
            <button
              type="button"
              onClick={onSkip}
              disabled={busy}
              className="mt-3 w-full rounded-[14px] py-3 text-[14px] font-medium"
              style={{ color: c.inkMuted, fontFamily: typ.body }}
            >
              {replayMode ? 'Back to Chancey' : 'Maybe later'}
            </button>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default OnboardingPage;
